import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** React 19 passes refs as ordinary props; this reaches the underlying button. */
  readonly ref?: Ref<HTMLButtonElement>;
  readonly variant?: Variant;
  readonly size?: 'md' | 'sm';
  readonly icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[variant],
    size === 'sm' ? styles.small : '',
    className ?? '',
  ].join(' ');
  return (
    <button type={type} className={classes} {...rest}>
      {icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  /** Accessible name; also shown as a tooltip. Required because the button has no text. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly size?: 'md' | 'sm';
  /** For toggle buttons: exposes aria-pressed. */
  readonly pressed?: boolean;
}

export function IconButton({
  label,
  icon,
  size = 'md',
  pressed,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const classes = [
    styles.iconButton,
    size === 'sm' ? styles.iconButtonSmall : '',
    className ?? '',
  ].join(' ');
  return (
    <button
      type={type}
      className={classes}
      aria-label={label}
      title={rest.title ?? label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      {...rest}
    >
      {icon}
    </button>
  );
}
